/**
 * API client — composes the live `api` surface by per-route choice
 * between `./mock/*` and `./real/*` implementations.
 *
 * Mode selection:
 *   - `VITE_API_MODE=mock` — every function routes to the in-memory
 *     mock under `./mock/`. Safe offline, used by the Playwright E2E
 *     suite and local dev without a server. This is the default for
 *     dev/test builds; a PRODUCTION build with the var unset defaults
 *     to `real` instead (G-010 fail-closed — see `resolveMode`).
 *   - `VITE_API_MODE=real` — the Phase 4.1 routes are swapped for
 *     live HTTP calls against `gi-api-server`; every deferred route
 *     (vault snapshot, marketplace, blueprint, attestations,
 *     connectors, coach, command dashboard) stays on the mock
 *     until its server-side route lands. Real mode REQUIRES a
 *     running `gi-api-server` reachable at `VITE_API_BASE_URL`
 *     (default `http://localhost:3000`); without one, every real
 *     call surfaces as a network error.
 *
 * Four surfaces — `createConsent`, `getConsent`, `revokeConsent`,
 * `verifyZkProof` — are real-only. They throw a clear error in
 * mock mode so a call site that hasn't been given a real-mode
 * deployment fails loudly instead of silently hitting a stub.
 */

import * as mock from './mock/handlers';
import { listAttestations } from './mock/attestation';
import {
  listBlueprints as mockListBlueprints,
  publishBlueprintAsConductor as mockPublishBlueprintAsConductor,
  saveBlueprintDraft,
} from './mock/blueprint';
import { askCoach } from './mock/coach';
import { getCommandSnapshot } from './mock/command';
import {
  createConnector,
  deleteConnector,
  getConnector,
  listConnectors,
  listConnectorsForGi,
  resetConnectors,
  testConnector,
  updateConnector,
} from './mock/connectors';
import {
  getCreatorProfile as mockGetCreatorProfile,
  getMarketplaceListing as mockGetMarketplaceListing,
  getProductBySlug as mockGetProductBySlug,
  listMarketplace as mockListMarketplace,
  publishToMarketplace as mockPublishToMarketplace,
  resetMarketplace,
  subscribe as mockSubscribe,
} from './mock/marketplace';
import { createGi as mockCreateGi } from './mock/spark';
import {
  BundleParseError,
  parseBundleBytes,
  verifyBundle,
  verifyById as mockVerifyById,
} from './mock/verify';

import { createGi as realCreateGi } from './real/spark';
import { getGiDetail as realGetGiDetail } from './real/gi';
import { verifyById as realVerifyById } from './real/verify';
import {
  getCreatorProfile as realGetCreatorProfile,
  getMarketplaceListing as realGetMarketplaceListing,
  getProductBySlug as realGetProductBySlug,
  listMarketplace as realListMarketplace,
  publishToMarketplace as realPublishToMarketplace,
  subscribe as realSubscribe,
} from './real/marketplace';
import {
  instantiateBlueprint as realInstantiateBlueprint,
  listBlueprints as realListBlueprints,
  publishBlueprintAsConductor as realPublishBlueprintAsConductor,
} from './real/blueprint';
import {
  getAttestation as realGetAttestation,
  verifyAttestation as realVerifyAttestation,
} from './real/attestation';
import { createConsent, getConsent, revokeConsent } from './real/consent';
import { previewGi as realPreviewGi } from './real/preview';
import { spawnChildGi as realSpawnChildGi } from './real/spawn';
import { verifyZkProof } from './real/zk';
import { getVaultSnapshot as realGetVaultSnapshot } from './real/vault';
import {
  createTrigger as realCreateTrigger,
  fireTrigger as realFireTrigger,
  getTriggerHealth as realGetTriggerHealth,
  getTriggerRecentEvents as realGetTriggerRecentEvents,
  listTriggers as realListTriggers,
  setTriggerEnabled as realSetTriggerEnabled,
  validateCronExpression as realValidateCronExpression,
} from './real/triggers';
import {
  createTrigger as mockCreateTrigger,
  fireTrigger as mockFireTrigger,
  getTriggerHealth as mockGetTriggerHealth,
  getTriggerRecentEvents as mockGetTriggerRecentEvents,
  listTriggers as mockListTriggers,
  setTriggerEnabled as mockSetTriggerEnabled,
  validateCronExpression as mockValidateCronExpression,
} from './mock/triggers';
import { getOmegaLogTail as realGetOmegaLogTail, connectOmegaLogStream } from './real/omegaLog';
import { getOmegaLogTail as mockGetOmegaLogTail } from './mock/omegaLog';
import { getBriefingToday as realGetBriefingToday, getBriefingHistory as realGetBriefingHistory } from './real/briefing';
import { getBriefingToday as mockGetBriefingToday, getBriefingHistory as mockGetBriefingHistory } from './mock/briefing';
import {
  getBrainStatus as realGetBrainStatus,
  getProposals as realGetProposals,
  getPredictionsAccuracy as realGetPredictionsAccuracy,
} from './real/brain';
import {
  getBrainStatus as mockGetBrainStatus,
  getProposals as mockGetProposals,
  getPredictionsAccuracy as mockGetPredictionsAccuracy,
} from './mock/brain';

export type ApiMode = 'mock' | 'real';

/** Mode resolution, in priority order:
 *    1. `localStorage['gi-engine.api-mode']` — runtime override, used
 *       by Playwright tests that need to flip to real mode per-page
 *       without a dev-server restart. Ignored in production builds
 *       that run without a writable localStorage.
 *    2. `VITE_API_MODE` — static build-time default from `.env`.
 *    3. Build-profile default (G-010 fail-closed): a PRODUCTION build
 *       with `VITE_API_MODE` unset defaults to `real`, never `mock`. A
 *       deploy that forgets the var must surface loud network errors,
 *       not silently serve fabricated mock data to the Founder. Dev /
 *       test builds keep the offline-friendly `mock` default. */
const mode: ApiMode = resolveMode();

function resolveMode(): ApiMode {
  try {
    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('gi-engine.api-mode')
        : null;
    if (stored === 'mock' || stored === 'real') return stored;
  } catch {
    /* localStorage may be unavailable in SSR / sandboxed iframes — fall through */
  }
  const env = import.meta.env.VITE_API_MODE as ApiMode | undefined;
  if (env === 'mock' || env === 'real') return env;
  // G-010 fail-closed: production builds default to real so a
  // misconfigured deploy fails loudly instead of fabricating data.
  return import.meta.env.PROD ? 'real' : 'mock';
}

if (mode !== 'mock' && mode !== 'real') {
  throw new Error(
    `Unknown VITE_API_MODE ${String(mode)}; expected "mock" or "real".`,
  );
}

// Per-mode wiring of the swap-capable Phase 4.x routes.
const createGi = mode === 'real' ? realCreateGi : mockCreateGi;
const getGiDetail = mode === 'real' ? realGetGiDetail : mock.getGiDetail;
const verifyById = mode === 'real' ? realVerifyById : mockVerifyById;
// Phase 4.7 (MARKETPLACE-BACKEND-WIRING-SPEC §5.1) — marketplace is
// real-only now that `gi-api-server` auto-seeds 65 engine-curated
// starter recipes at boot. The mock catalog stays available for
// frontend-only iteration via the explicit
// `localStorage['gi-engine.api-mode'] = 'mock-marketplace'` override
// below; the silent fallback that masked backend regressions is
// gone. The endpoint façade (`api/endpoints/marketplace.ts`) still
// folds in the six "Official Products" alongside whatever the
// backend returns.
const useMockMarketplace = (() => {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('gi-engine.api-mode') === 'mock-marketplace'
    );
  } catch {
    return false;
  }
})();
const listMarketplace = useMockMarketplace ? mockListMarketplace : realListMarketplace;
const publishToMarketplace = useMockMarketplace
  ? mockPublishToMarketplace
  : realPublishToMarketplace;
const getMarketplaceListing = useMockMarketplace
  ? mockGetMarketplaceListing
  : realGetMarketplaceListing;
const getProductBySlug = useMockMarketplace ? mockGetProductBySlug : realGetProductBySlug;
const subscribe = useMockMarketplace ? mockSubscribe : realSubscribe;
const getCreatorProfile = useMockMarketplace ? mockGetCreatorProfile : realGetCreatorProfile;
const listBlueprints = mode === 'real' ? realListBlueprints : mockListBlueprints;
const publishBlueprintAsConductor =
  mode === 'real' ? realPublishBlueprintAsConductor : mockPublishBlueprintAsConductor;

// Vault snapshot now flips with the api-mode. Server-side Vault is
// the durable replacement for the localStorage-only Vault; mock
// stays available for offline / Playwright runs. The `'mock-vault'`
// localStorage override below mirrors the marketplace pattern so
// frontend-only iteration can pin to mock without a full mode flip.
const useMockVault = (() => {
  try {
    return (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('gi-engine.api-mode') === 'mock-vault'
    );
  } catch {
    return false;
  }
})();
const getVaultSnapshot =
  mode === 'real' && !useMockVault ? realGetVaultSnapshot : mock.getVaultSnapshot;

export const api = {
  // ── Vault snapshot ─────────────────────────────────────────────
  // - real: GET /api/v1/vault, JWT-scoped, durable across browsers
  // - mock: localStorage snapshot (what the UI used to do exclusively)
  getVaultSnapshot,
  seedDemoData: mock.seedDemoData,
  resetMock: mock.resetMock,

  // ── Per-mode Phase 4.1 routes ──
  createGi,
  getGiDetail,
  verifyById,

  // ── Client-side only (bundle parse + bundle cross-check) ──
  verifyBundle,
  parseBundleBytes,

  // ── Swap-capable via dispatch above: marketplace + blueprints ──
  listMarketplace,
  publishToMarketplace,
  getMarketplaceListing,
  getProductBySlug,
  subscribe,
  getCreatorProfile,
  listBlueprints,
  publishBlueprintAsConductor,

  // ── Deferred — always mock (no server route, or intentionally
  //    local). `saveBlueprintDraft` stays local: it writes to
  //    `localStorage` and is not a server concept. ──
  resetMarketplace,
  saveBlueprintDraft,
  listAttestations,
  getCommandSnapshot,
  askCoach,
  listConnectors,
  listConnectorsForGi,
  getConnector,
  createConnector,
  updateConnector,
  deleteConnector,
  testConnector,
  resetConnectors,

  // ── Trigger engine (Phase 6 + 11) — swap-capable ──
  listTriggers: mode === 'real' ? realListTriggers : mockListTriggers,
  getTriggerHealth: mode === 'real' ? realGetTriggerHealth : mockGetTriggerHealth,
  getTriggerRecentEvents: mode === 'real' ? realGetTriggerRecentEvents : mockGetTriggerRecentEvents,
  createTrigger: mode === 'real' ? realCreateTrigger : mockCreateTrigger,
  setTriggerEnabled: mode === 'real' ? realSetTriggerEnabled : mockSetTriggerEnabled,
  fireTrigger: mode === 'real' ? realFireTrigger : mockFireTrigger,
  validateCronExpression: mode === 'real' ? realValidateCronExpression : mockValidateCronExpression,

  // ── OMEGA log tail + SSE stream (Phase 14) ──
  getOmegaLogTail: mode === 'real' ? realGetOmegaLogTail : mockGetOmegaLogTail,
  // connectOmegaLogStream is real-only: SSE requires a live HTTP server.
  // In mock mode the page falls back to polling getOmegaLogTail.
  connectOmegaLogStream: mode === 'real' ? connectOmegaLogStream : null,

  // ── Founder Daily Briefing (Phase 19) ──────────────────────────
  getBriefingToday: mode === 'real' ? realGetBriefingToday : mockGetBriefingToday,
  getBriefingHistory: mode === 'real' ? realGetBriefingHistory : mockGetBriefingHistory,

  // ── OMEGA Brain status + proposals (Phase 25) ───────────────────
  getBrainStatus: mode === 'real' ? realGetBrainStatus : mockGetBrainStatus,
  getProposals: mode === 'real' ? realGetProposals : mockGetProposals,
  getPredictionsAccuracy: mode === 'real' ? realGetPredictionsAccuracy : mockGetPredictionsAccuracy,

  // ── Real-only surfaces. Throw in mock mode. ──
  createConsent: mode === 'real' ? createConsent : realOnlyStub('createConsent'),
  getConsent: mode === 'real' ? getConsent : realOnlyStub('getConsent'),
  revokeConsent: mode === 'real' ? revokeConsent : realOnlyStub('revokeConsent'),
  verifyZkProof: mode === 'real' ? verifyZkProof : realOnlyStub('verifyZkProof'),
  instantiateBlueprint:
    mode === 'real' ? realInstantiateBlueprint : realOnlyStub('instantiateBlueprint'),
  getAttestation: mode === 'real' ? realGetAttestation : realOnlyStub('getAttestation'),
  verifyAttestation:
    mode === 'real' ? realVerifyAttestation : realOnlyStub('verifyAttestation'),
  spawnChildGi: mode === 'real' ? realSpawnChildGi : realOnlyStub('spawnChildGi'),
  previewGi: mode === 'real' ? realPreviewGi : realOnlyStub('previewGi'),
};

export { BundleParseError };
export { ApiHttpError, apiBaseUrl, probeHealth } from './http';

export type Api = typeof api;

/** Current API mode — useful for dev banners / diagnostic UIs. */
export function apiMode(): ApiMode {
  return mode;
}

function realOnlyStub(name: string): (...args: never[]) => never {
  return () => {
    throw new Error(
      `api.${name}() requires VITE_API_MODE=real — set it in your .env and restart the dev server.`,
    );
  };
}
